import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getChessLeaderboard,
  getMyChessRating,
} from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import UserLink from '../../components/common/UserLink';
import Button from '../../components/common/Button';
import { PlayingBanner, PlayingEmptyState, PlayingSkeleton } from './playingUi';

const SPEED_TABS = ['BULLET', 'BLITZ', 'RAPID', 'CLASSICAL', 'CORRESPONDENCE'];

function parseSpeed(raw) {
  const v = String(raw || '').toUpperCase();
  return SPEED_TABS.includes(v) ? v : 'BLITZ';
}

export default function LeaderboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [speedClass, setSpeedClass] = useState(() => parseSpeed(searchParams.get('speed')));
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [meRatingSoftFail, setMeRatingSoftFail] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const syncUrl = (nextSpeed) => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    if (nextSpeed === 'BLITZ') next.delete('speed');
    else next.set('speed', nextSpeed);
    setSearchParams(next, { replace: true });
  };

  const changeSpeed = (sc) => {
    setSpeedClass(sc);
    syncUrl(sc);
  };

  useEffect(() => {
    const urlSpeed = parseSpeed(searchParams.get('speed'));
    setSpeedClass((prev) => (prev === urlSpeed ? prev : urlSpeed));
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setMeRatingSoftFail(false);
    let meFailed = false;
    Promise.all([
      getChessLeaderboard(speedClass),
      getMyChessRating(speedClass).catch(() => { meFailed = true; return null; }),
    ])
      .then(([data, mine]) => {
        setRows(Array.isArray(data) ? data : []);
        setMe(mine);
        setMeRatingSoftFail(meFailed);
      })
      .catch(() => setError(t('playing.loadFailed')))
      .finally(() => setLoading(false));
  }, [t, speedClass, reloadTick]);

  const myUserId = me?.userId ?? null;
  const meInList = myUserId != null && rows.some((r) => Number(r.userId) === Number(myUserId));

  return (
    <div className="page-shell-form playing-page max-w-3xl">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.leaderboard')}
        subtitle={t('playing.leaderboardSubtitle')}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        {SPEED_TABS.map((sc) => (
          <button
            key={sc}
            type="button"
            onClick={() => changeSpeed(sc)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              speedClass === sc ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700'
            }`}
          >
            {t(`playing.speed.${sc}`, {
              defaultValue: sc.charAt(0) + sc.slice(1).toLowerCase(),
            })}
          </button>
        ))}
      </div>
      {error && (
        <PlayingBanner tone="danger">
          <p>{error}</p>
          <div className="mt-2">
            <Button variant="page-secondary" onClick={() => setReloadTick((n) => n + 1)}>
              {t('playing.retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        </PlayingBanner>
      )}
      {meRatingSoftFail && !error && (
        <PlayingBanner tone="warning">
          {t('playing.myRatingLoadFailed', {
            defaultValue: 'Could not load your rating highlight. The leaderboard below may still be complete.',
          })}
        </PlayingBanner>
      )}

      <div className="list-table-card overflow-hidden">
        {loading ? (
          <div className="p-4">
            <PlayingSkeleton rows={6} />
          </div>
        ) : rows.length === 0 && !error ? (
          <PlayingEmptyState
            title={t('playing.noRatingsYet')}
            hint={t('playing.noRatingsHint')}
            action={(
              <Button
                variant="page-primary"
                onClick={() => navigate('/playing')}
              >
                {t('playing.playNow')}
              </Button>
            )}
          />
        ) : (
          <>
            <div className="responsive-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">#</th>
                    <th className="px-4 py-3 text-left font-semibold">{t('playing.player')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('playing.rating')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('playing.games')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('playing.recordShort')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r, idx) => {
                    const isYou = myUserId != null && Number(r.userId) === Number(myUserId);
                    return (
                      <tr
                        key={r.userId}
                        className={
                          isYou
                            ? 'bg-primary-50/80 ring-1 ring-inset ring-primary-200'
                            : idx < 3
                              ? 'bg-amber-50/40'
                              : 'hover:bg-slate-50/80'
                        }
                      >
                        <td className="px-4 py-3">
                          <span className={`playing-rank ${idx < 3 || isYou ? 'playing-rank--top' : ''}`}>
                            {r.rank}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          <UserLink
                            id={r.userId}
                            name={r.displayName}
                            playing
                            className="text-slate-900 hover:text-primary-700"
                          />
                          {isYou && (
                            <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-primary-700">
                              {t('playing.you')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-base font-bold tabular-nums text-slate-900">
                          {r.rating}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                          {r.gamesPlayed}
                        </td>
                        <td className="px-4 py-3 text-right text-xs tabular-nums text-slate-500">
                          {`${r.wins ?? 0}/${r.draws ?? 0}/${r.losses ?? 0}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {me && !meInList && (
              <div className="border-t border-primary-100 bg-primary-50/70 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="playing-rank playing-rank--top">{me.rank ?? '—'}</span>
                    <div>
                      <p className="font-semibold text-slate-900">
                        <UserLink
                          id={me.userId}
                          name={me.displayName || t('playing.you')}
                          playing
                          className="text-slate-900 hover:text-primary-700"
                        />
                        <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-primary-700">
                          {t('playing.you')}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500">{t('playing.outsideTop')}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 tabular-nums text-slate-700">
                    <span className="font-bold text-slate-900">{me.rating}</span>
                    <span>
                      {`${me.gamesPlayed ?? 0} ${t('playing.games').toLowerCase()}`}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
